import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { installChunkErrorHandlers } from "@/components/ErrorBoundary";
import { Capacitor } from "@capacitor/core";
import { SplashScreen as NativeSplash } from "@capacitor/splash-screen";

// Native launch screen stays up (launchAutoHide=false) until the web view has
// painted the matching boot backdrop — then it fades out with no white frame.
if (Capacitor.isNativePlatform()) {
  requestAnimationFrame(() => NativeSplash.hide({ fadeOutDuration: 250 }).catch(() => {}));
  setTimeout(() => NativeSplash.hide().catch(() => {}), 4000);
}

// "ResizeObserver loop completed with undelivered notifications" is a
// benign browser warning that Radix Select (and any component using
// ResizeObserver) can emit when it measures its content on open. CRA's
// react-error-overlay treats it as an uncaught runtime error and covers
// the whole page. Two-layer fix:
//   1) Wrap the native ResizeObserver so the callback runs on the next
//      animation frame, which prevents the loop entirely.
//   2) Belt & braces: also intercept the `error`/`unhandledrejection`
//      events in CAPTURE phase so if the wrapper misses one, it never
//      reaches the CRA overlay listener.
if (typeof window !== "undefined") {
  // 1) Debounce ResizeObserver callbacks — the exact recommended fix
  // from the WICG spec.
  if (typeof window.ResizeObserver !== "undefined") {
    const OriginalRO = window.ResizeObserver;
    window.ResizeObserver = class DebouncedResizeObserver extends OriginalRO {
      constructor(callback) {
        super((entries, observer) => {
          window.requestAnimationFrame(() => {
            try {
              callback(entries, observer);
            } catch (_) {
              /* swallow observer callback failures — never crash the app */
            }
          });
        });
      }
    };
  }

  // 2) Capture-phase swallow for any that still slip through.
  const swallowResizeObserverLoop = (e) => {
    const msg =
      (e && e.message) ||
      (e && e.reason && (e.reason.message || String(e.reason))) ||
      "";
    if (typeof msg === "string" && msg.includes("ResizeObserver loop")) {
      e.stopImmediatePropagation && e.stopImmediatePropagation();
      e.preventDefault && e.preventDefault();
      return false;
    }
    return undefined;
  };
  window.addEventListener("error", swallowResizeObserverLoop, true);
  window.addEventListener("unhandledrejection", swallowResizeObserverLoop, true);
}

installChunkErrorHandlers();
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
